// src/components/MatchList.jsx
import React, { useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarDays,
  Clock3,
  EllipsisVertical,
  MapPin,
  Play,
  Trash2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import TeamLogo from "@/components/TeamLogo";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { getMatchById } from "@/api/matches";
import { useRefreshMatchLineup, useRestartMatchLineup } from "@/queries/matches";
import { describeMatchStart, getMatchTeams, getMatchVenue } from "@/utils/matchFormatting";

function getHeatProgress(match) {
  const heats = Array.isArray(match?.heats) ? match.heats : [];
  const total = heats.length || 15;

  const done = heats.reduce((acc, heat) => {
    const status = (heat?.status || "").toString().toLowerCase();
    const isCompleted = status === "completed";
    const hasResults = Array.isArray(heat?.results) && heat.results.length > 0;
    return acc + (isCompleted || hasResults ? 1 : 0);
  }, 0);

  const value = total ? Math.round((done / total) * 100) : 0;
  return { done, total, value };
}

function resolveStatus(match, startInfo) {
  const status = (match?.status || "").toString().toLowerCase();

  if (status === "live") return { label: "Live", variant: "destructive" };
  if (status === "confirmed") return { label: "Bekräftad", variant: "default" };
  if (status === "reopened") return { label: "Återöppnat", variant: "default" };
  if (status === "completed") return { label: "Avslutad", variant: "secondary" };
  if (startInfo?.isToday) return { label: "Idag", variant: "secondary" };
  if (startInfo?.isPast) return { label: "Avslutad", variant: "outline" };
  return { label: "Kommande", variant: "outline" };
}

function TeamColumn({ teamName, fallbackLetter }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 text-center">
      <div className="relative h-11 w-11 shrink-0 rounded-full border border-border/70 bg-muted/30 sm:h-12 sm:w-12">
        <TeamLogo
          teamName={teamName}
          size={34}
          className="absolute inset-0 m-auto h-[34px] w-[34px] object-contain sm:h-9 sm:w-9"
          fallback={
            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold uppercase text-muted-foreground">
              {fallbackLetter}
            </div>
          }
        />
      </div>
      <span className="max-w-full text-xs font-semibold text-foreground sm:text-sm">{teamName}</span>
    </div>
  );
}

function splitStartParts(startInfo) {
  return {
    datePart: startInfo?.dateLabel || "Datum saknas",
    timePart: startInfo?.timeLabel || "",
  };
}

function CreatedMatchCard({ match, onDelete, onOpenMatch }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const startInfo = describeMatchStart(match);
  const venue = getMatchVenue(match);
  const { home, away } = getMatchTeams(match);
  const status = resolveStatus(match, startInfo);
  const { done, total, value } = getHeatProgress(match);
  const hasProgress = total > 0;
  const hasResults = Array.isArray(match?.heats)
    ? match.heats.some((heat) => Array.isArray(heat?.results) && heat.results.length > 0)
    : false;
  const hasLineupAttention = match?.lineup_sync?.status === "outdated";
  const score = `${match?.home_score ?? 0} - ${match?.away_score ?? 0}`;
  const { datePart, timePart } = splitStartParts(startInfo);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenMatch(match)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenMatch(match);
          }
        }}
        className="protocol-list-card protocol-list-card-created group rounded-xl border bg-card p-2.5 text-left transition hover:border-primary/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-3"
      >
        <div className="relative flex items-center justify-between gap-2">
          <div>
            {status?.label ? (
              <Badge className="native-v3-status-badge" variant={status.variant}>
                {status.label}
              </Badge>
            ) : null}
          </div>
          <div className="pointer-events-none absolute inset-x-0 flex justify-center">
            {timePart ? (
              <div className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground sm:text-sm">
                <Clock3 className="h-4 w-4" />
                <span>{timePart}</span>
              </div>
            ) : null}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="native-v3-protocol-menu-trigger h-8 w-8 rounded-full"
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <EllipsisVertical className="h-4 w-4" />
                <span className="sr-only">Fler val</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="native-v3-protocol-menu w-48 border-border bg-background shadow-md"
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <DropdownMenuItem
                className="native-v3-protocol-menu-item"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenMatch(match);
                }}
              >
                <Play className="h-4 w-4" />
                {hasResults ? "Fortsätt protokoll" : "Starta protokoll"}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="native-v3-protocol-menu-item native-v3-protocol-menu-item-danger text-destructive focus:text-destructive"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Ta bort
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-1 flex items-start gap-2">
          <TeamColumn teamName={home} fallbackLetter="H" />

          <div className="flex shrink-0 flex-col items-center justify-center gap-1 px-1 text-center">
            <div className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {score}
            </div>
            <div className="text-xs text-muted-foreground sm:text-sm">
              {done} av {total} heat klara
            </div>
          </div>

          <TeamColumn teamName={away} fallbackLetter="B" />
        </div>

        {hasProgress ? (
          <div className="mt-3">
            <div className="flex items-center gap-3">
              <Progress value={value} className="h-2 flex-1" />
              <span className="min-w-10 text-right text-xs font-medium text-muted-foreground sm:text-sm">
                {value}%
              </span>
            </div>
          </div>
        ) : null}

        <div className="mt-3 border-t border-border/70 pt-2.5 text-xs text-muted-foreground sm:text-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <CalendarDays className="h-4 w-4 shrink-0" />
              <span>{datePart}</span>
              {venue ? (
                <>
                  <span>•</span>
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{venue}</span>
                </>
              ) : null}
            </div>

            {hasLineupAttention ? (
              <div className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" />
                Laguppställningen har ändrats
              </div>
            ) : (
              <div className="min-w-0 flex-1" />
            )}

            <Button
              onClick={(event) => {
                event.stopPropagation();
                onOpenMatch(match);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              size="sm"
              className="shrink-0 gap-1.5"
            >
              <Play className="h-4 w-4" />
              <span className="hidden sm:inline">{hasResults ? "Fortsätt" : "Starta"}</span>
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort match?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta går inte att ångra. Protokollet kommer att raderas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(match.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function MatchList({ matches, userId, onDelete }) {
  const { requestProtocolSession } = useProtocolShell();
  const refreshLineup = useRefreshMatchLineup();
  const restartLineup = useRestartMatchLineup();
  const [resumeDecision, setResumeDecision] = useState(null);
  const myMatches = matches.filter((match) => userId && match.created_by === userId);

  const openProtocol = React.useCallback(
    (matchId) => {
      requestProtocolSession(matchId);
    },
    [requestProtocolSession]
  );

  const handleOpenMatch = React.useCallback(
    async (match) => {
      if (!match?.id) return;
      try {
        const detailed = await getMatchById(match.id);
        const lineupSync = detailed?.lineup_sync || null;
        if (lineupSync?.status === "outdated") {
          const startInfo = describeMatchStart(match);
          const teams = getMatchTeams(match);
          setResumeDecision({
            matchId: match.id,
            title: `${teams.home} vs ${teams.away}`,
            startLabel: [startInfo?.dateLabel, startInfo?.timeLabel].filter(Boolean).join(" • "),
            lineupSync,
          });
          return;
        }
        openProtocol(match.id);
      } catch (error) {
        toast.error(error?.message || "Kunde inte öppna protokollet.");
      }
    },
    [openProtocol]
  );

  const handleRefreshAndOpen = React.useCallback(async () => {
    const matchId = resumeDecision?.matchId;
    if (!matchId) return;
    const promise = refreshLineup.mutateAsync(matchId);
    toast.promise(promise, {
      loading: "Uppdaterar protokollet...",
      success: "Protokollet uppdaterades till senaste lineup.",
      error: (error) => error?.message || "Kunde inte uppdatera protokollet.",
    });
    try {
      await promise;
      setResumeDecision(null);
      openProtocol(matchId);
    } catch {
      return;
    }
  }, [openProtocol, refreshLineup, resumeDecision?.matchId]);

  const handleRestartAndOpen = React.useCallback(async () => {
    const matchId = resumeDecision?.matchId;
    if (!matchId) return;
    const promise = restartLineup.mutateAsync(matchId);
    toast.promise(promise, {
      loading: "Börjar om protokollet...",
      success: "Protokollet återställdes till senaste lineup.",
      error: (error) => error?.message || "Kunde inte börja om protokollet.",
    });
    try {
      await promise;
      setResumeDecision(null);
      openProtocol(matchId);
    } catch {
      return;
    }
  }, [openProtocol, restartLineup, resumeDecision?.matchId]);

  return (
    <>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {myMatches.map((match) => (
          <CreatedMatchCard
            key={match.id}
            match={match}
            onDelete={onDelete}
            onOpenMatch={handleOpenMatch}
          />
        ))}

        {myMatches.length === 0 ? (
          <p className="col-span-full py-8 text-center text-muted">Inga skapade matcher ännu</p>
        ) : null}
      </div>

      <AlertDialog
        open={Boolean(resumeDecision)}
        onOpenChange={(open) => !open && setResumeDecision(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resumeDecision?.lineupSync?.can_refresh
                ? "Laguppställningen har ändrats"
                : "Protokollet bygger på äldre laguppställning"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="mb-2 block font-medium text-foreground">
                {resumeDecision?.title}
                {resumeDecision?.startLabel ? ` • ${resumeDecision.startLabel}` : ""}
              </span>
              {resumeDecision?.lineupSync?.can_refresh
                ? "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Eftersom inga heat har körts ännu kan protokollet uppdateras till senaste version."
                : "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Du kan fortsätta med nuvarande protokoll eller börja om med senaste lineup."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={refreshLineup.isPending || restartLineup.isPending}>
              Avbryt
            </AlertDialogCancel>
            {resumeDecision?.lineupSync?.can_refresh ? (
              <AlertDialogAction onClick={handleRefreshAndOpen} disabled={refreshLineup.isPending}>
                {refreshLineup.isPending ? "Uppdaterar..." : "Uppdatera protokoll"}
              </AlertDialogAction>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    openProtocol(resumeDecision.matchId);
                    setResumeDecision(null);
                  }}
                  disabled={restartLineup.isPending}
                >
                  Fortsätt ändå
                </Button>
                <AlertDialogAction onClick={handleRestartAndOpen} disabled={restartLineup.isPending}>
                  {restartLineup.isPending ? "Börjar om..." : "Börja om protokoll"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
